import React, { SyntheticEvent, useState, useEffect } from 'react';
import { Grid, Image, Icon, Button, Modal, Header } from 'semantic-ui-react';
import { Field } from 'formik';
import { FormikErrorMessage } from '../../../components/ErrorMessage';
import { FieldLabel } from '../../../styles';
import { EstateFormProps } from '../UpdateEstateForm';
import Dropdown from '../../../components/Dropdown';
import { CustoDropDownItemProps } from '../../../components/types';
import { dangerNotification } from '../../../services/notification';
import { list } from '../../../services/insurer';
import Toggle from '../../../components/Toggle';
import { InsurersImageWrapper } from '../styles';
import { InsurerForUpdateEstateForm } from '../types';
import { InsurersCodeEnum } from '../../../enums/insurers-code.enum';
import Input from '../../../components/Input';
import { AnalysisAssuranceEnum } from '../../../enums/analysis-assurance.enum';
import { hasPermission } from '../../../components/AuthorizationContainer';
import { ActionEnum } from '../../../enums/authz-action.enum';
import { FeatureEnum } from '../../../enums/authz-feature.enum';
import IconButton from '../../../components/IconButton';
import { CreditasConfigModal } from '../../../components/CreditasConfigModal';

export const BondInsuranceData = (props: EstateFormProps) => {
  const { handleInputChange, values, setFieldValue } = props;
  const [availableInsurers, setAvailableInsurers] = useState<any[]>([]);
  const [selectedInsurer, setSelectedInsurer] = useState<any>();
  const [userCanEdit, setUserCanEdit] = useState<boolean>(false);

  const sanitizePercentageInput = (value: string) => {
    const normalizedValue = String(value ?? '')
      .replace(/,/g, '.')
      .replace(/[^\d.]/g, '');
    const firstDecimalSeparatorIndex = normalizedValue.indexOf('.');

    if (firstDecimalSeparatorIndex === -1) {
      return normalizedValue;
    }

    const integerPart = normalizedValue.slice(0, firstDecimalSeparatorIndex);
    const decimalPart = normalizedValue
      .slice(firstDecimalSeparatorIndex + 1)
      .replace(/\./g, '')
      .slice(0, 2);

    return `${integerPart}.${decimalPart}`;
  };

  const formatPercentageInput = (value: string) => {
    const sanitizedValue = sanitizePercentageInput(value);

    if (!sanitizedValue || sanitizedValue === '.') {
      return '';
    }

    const numericValue = Number(sanitizedValue);

    if (!Number.isFinite(numericValue)) {
      return '';
    }

    return Math.min(Math.max(numericValue, 0), 100).toFixed(2);
  };

  const handlePercentageChange = (data: any) => {
    setFieldValue(data.field.name, sanitizePercentageInput(data.value));
  };

  const handlePercentageBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(e.target.name, formatPercentageInput(e.target.value));
  };

  useEffect(() => {
    const has = hasPermission(ActionEnum.update, FeatureEnum.insurers);
    setUserCanEdit(has);
  }, []);

  const pottencialMultiplesOptions: CustoDropDownItemProps[] = [
    { key: 0, text: 'Indefinido', value: 0 },
    { key: 3, text: '3', value: 3 },
    { key: 6, text: '6', value: 6 },
    { key: 12, text: '12', value: 12 },
    { key: 15, text: '15', value: 15 },
    { key: 18, text: '18', value: 18 },
    { key: 24, text: '24', value: 24 },
    { key: 30, text: '30', value: 30 },
    { key: 45, text: '45', value: 45 }
  ];

  const garanttiLMIOptions: CustoDropDownItemProps[] = [
    { key: 0, text: 'Indefinido', value: 0 },
    { key: 12, text: '12X', value: 12 },
    { key: 18, text: '18X', value: 18 },
    { key: 20, text: '20X', value: 20 },
    { key: 30, text: '30X', value: 30 },
    { key: 36, text: '36X', value: 36 },
    { key: 40, text: '40X', value: 40 }
  ];

  const pottencialAssuranceOptions: CustoDropDownItemProps[] = [
    { key: 1, text: 'Simples', value: AnalysisAssuranceEnum.simple },
    { key: 2, text: 'Completo', value: AnalysisAssuranceEnum.complete }
  ];

  useEffect(() => {
    const hasPortoEssencial = values?.bondInsurers?.find((i: any) =>
      i.code.includes(InsurersCodeEnum.PortoEssencial)
    );

    if (hasPortoEssencial) {
      setFieldValue('portoCode', props.values.portoCode);
    }

    list({ availability: 'BOND_INSURANCE' })
      .then(result => {
        /// order result array by property orderPreference
        const hasOrder: any[] = [];
        const notHasOrder: any[] = [];

        if (result) {
          result.forEach(insurer => {
            let order = 0;
            if (insurer.code === InsurersCodeEnum.PortoEssencial) {
              order =
                values.insurerSpecificData?.portoEssencial?.bondInsurance
                  ?.orderPreference;
            } else if (insurer.code === InsurersCodeEnum.Garantti) {
              order =
                values.insurerSpecificData?.garantti?.bondInsurance?.orderPreference;
            } else if (insurer.code === InsurersCodeEnum.Pottencial) {
              order =
                values.insurerSpecificData?.pottencial?.bondInsurance?.orderPreference;
            } else if (insurer.code === InsurersCodeEnum.Too) {
              order =
                values.insurerSpecificData?.tooSeguros?.bondInsurance?.orderPreference;
            } else if (insurer.code === InsurersCodeEnum.TokioMarine) {
              order =
                values.insurerSpecificData?.tokioMarine?.bondInsurance?.orderPreference;
            } else if (insurer.code === InsurersCodeEnum.Porto) {
              order =
                values.insurerSpecificData?.portoTradicional?.bondInsurance
                  ?.orderPreference;
            }
            insurer.orderPreference = order;
            if (order > 0) {
              hasOrder.push(insurer);
            } else {
              notHasOrder.push(insurer);
            }
          });
        }
        hasOrder.sort((a, b) => {
          return a.orderPreference - b.orderPreference;
        });
        setAvailableInsurers([...hasOrder, ...notHasOrder]);
      })
      .catch(error => {
        dangerNotification(
          'Oops...',
          'Não foi possível buscar as seguradoras disponíveis'
        );
      })
      .finally(() => { });

    setFieldValue(
      'insurerSpecificData.garantti.bondInsurance.tariffPlan',
      props.values.insurerSpecificData.garantti.bondInsurance.garanttiPlan
    );
  }, []);

  const handleToggle = async (e: SyntheticEvent, value: any) => {
    if (value) {
      const insurer = availableInsurers.find(i => i.id == value.name);
      if (value.checked && insurer) {
        if (insurer.code === InsurersCodeEnum.Too) {
          setFieldValue('isTooSegurosBondInsuranceSelected', true);
        }

        if (insurer.code === InsurersCodeEnum.TokioMarine) {
          setFieldValue('isTokioMarineBondInsuranceSelected', true);
        }

        if (insurer.code === InsurersCodeEnum.Garantti) {
          return;
        }

        if (insurer.code === InsurersCodeEnum.Pottencial) {
          setFieldValue('isPottencialBondInsuranceSelected', true);
        }

        if (insurer.code === InsurersCodeEnum.Porto12) {
          setFieldValue('isPorto12xBondInsuranceSelected', true);
        }

        if (insurer.code === InsurersCodeEnum.Porto18) {
          setFieldValue('isPorto18xBondInsuranceSelected', true);
        }

        if (insurer.code === InsurersCodeEnum.Porto20) {
          setFieldValue('isPorto20xBondInsuranceSelected', true);
        }

        if (insurer.code === InsurersCodeEnum.Porto30) {
          setFieldValue('isPorto30xBondInsuranceSelected', true);
        }

        if (insurer.code === InsurersCodeEnum.PortoEssencial) {
          setFieldValue('isPortoEssencialBondInsuranceSelected', true);
        }

        if (insurer.code === InsurersCodeEnum.Porto) {
          setFieldValue('isPortoTradicionalBondInsuranceSelected', true);
        }

        if (insurer.code === InsurersCodeEnum.Creditas) {
          setFieldValue('isCreditasBondInsuranceSelected', true);
        }

        const insurerForForm: InsurerForUpdateEstateForm = {
          id: insurer.id,
          code: insurer.code,
          name: insurer.name,
          imageURL: insurer.image
        };
        values.bondInsurers.push(insurerForForm);
      } else if (!value.checked && insurer) {
        if (insurer.code === InsurersCodeEnum.Too) {
          setFieldValue('isTooSegurosBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.TokioMarine) {
          setFieldValue('isTokioMarineBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.Garantti) {
          setFieldValue('isGaranttiBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.Pottencial) {
          setFieldValue('isPottencialBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.Porto12) {
          setFieldValue('isPorto12xBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.Porto18) {
          setFieldValue('isPorto18xBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.Porto20) {
          setFieldValue('isPorto20xBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.Porto30) {
          setFieldValue('isPorto30xBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.PortoEssencial) {
          setFieldValue('isPortoEssencialBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.Porto) {
          setFieldValue('isPortoTradicionalBondInsuranceSelected', false);
        }

        if (insurer.code === InsurersCodeEnum.Creditas) {
          setFieldValue('isCreditasBondInsuranceSelected', false);
        }

        values.bondInsurers = values.bondInsurers.filter((i: any) => i.id != value.name);
      }
      setFieldValue(`values.bondInsurers`, values.bondInsurers);
    }
  };

  const [openModalConfig, setOpenModalConfig] = useState(false);

  const BondInsuranceConfigPottencialContentModal = () => {
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>
              Taxa Comissão
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.pottencial.bondInsurance.commissionPercentage"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>

          <Grid.Column width={5}>
            <FieldLabel>
              Multiplo
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.pottencial.bondInsurance.multiple"
              disabled={!userCanEdit}
              options={pottencialMultiplesOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>

          <Grid.Column width={5}>
            <FieldLabel>
              Tipo de coberturas
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.pottencial.bondInsurance.assurance"
              disabled={!userCanEdit}
              options={pottencialAssuranceOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>
              Taxa Residencial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.pottencial.bondInsurance.pfResidentialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>

          <Grid.Column width={8}>
            <FieldLabel>
              Taxa Comercial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.pottencial.bondInsurance.pfComercialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>

          {/* <Grid.Column width={4}>
                          <FieldLabel>LMI
                            <span style={{ color: "red" }}>*</span></FieldLabel>
                          <Field
                            name="insurerSpecificData.pottencial.bondInsurance.lmi"
                            options={garanttiLMIOptions}
                            onChange={(e: SyntheticEvent, data: any) =>
                              handleInputChange(data, props)
                            }
                            component={Dropdown}
                            selection
                            compact
                            basic
                            fluid
                          />
                        </Grid.Column> */}
        </Grid.Row>
      </Grid>
    );
  };

  const BondInsuranceConfigPortoContentModal = () => {
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>
              Tipo de coberturas
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoTradicional.bondInsurance.assurance"
              disabled={!userCanEdit}
              options={pottencialAssuranceOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              LMI
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoTradicional.bondInsurance.lmi"
              disabled={!userCanEdit}
              options={garanttiLMIOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>Código na Seguradora</FieldLabel>
            <Field
              name="portoCode"
              disabled={!userCanEdit}
              onChange={(e: SyntheticEvent, data: any) => {
                if (data.value && data.value.length > 6) {
                  return;
                }
                handleInputChange(
                  { ...data, value: data.value.replace(/\D/g, '') },
                  props
                );
              }}
              component={Input}
              fluid
            />
            <FormikErrorMessage component="div" name="portoCode" />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>
              Taxa Comissão
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoTradicional.bondInsurance.commissionPercentage"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              Taxa Residencial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoTradicional.bondInsurance.pfResidentialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              Taxa Comercial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoTradicional.bondInsurance.pfComercialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  const BondInsuranceConfigPortoEssencialContentModal = () => {
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>Código na Seguradora</FieldLabel>
            <Field
              name="portoCode"
              disabled={!userCanEdit}
              onChange={(e: SyntheticEvent, data: any) => {
                if (data.value && data.value.length > 6) {
                  return;
                }
                handleInputChange(
                  { ...data, value: data.value.replace(/\D/g, '') },
                  props
                );
              }}
              component={Input}
              fluid
            />
            <FormikErrorMessage component="div" name="portoCode" />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              Tipo de coberturas
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoEssencial.bondInsurance.assurance"
              disabled={!userCanEdit}
              options={pottencialAssuranceOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              LMI
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoEssencial.bondInsurance.lmi"
              disabled={!userCanEdit}
              options={garanttiLMIOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>
              Taxa Comissão
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoEssencial.bondInsurance.commissionPercentage"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>

          <Grid.Column width={5}>
            <FieldLabel>
              Taxa Residencial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoEssencial.bondInsurance.pfResidentialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>

          <Grid.Column width={5}>
            <FieldLabel>
              Taxa Comercial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.portoEssencial.bondInsurance.pfComercialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  const BondInsuranceConfigTooContentModal = () => {
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>Código Segimob</FieldLabel>
            <Field
              name="segimobCode"
              disabled={!userCanEdit}
              onChange={(e: React.ChangeEvent<HTMLInputElement>, data: any) => {
                handleInputChange(data, props);
              }}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue('segimobCode', e.target.value);
              }}
              component={Input}
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              Tipo de coberturas
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tooSeguros.bondInsurance.assurance"
              disabled={!userCanEdit}
              options={pottencialAssuranceOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              LMI
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tooSeguros.bondInsurance.lmi"
              disabled={!userCanEdit}
              options={garanttiLMIOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>
              Taxa Comissão
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tooSeguros.bondInsurance.commissionPercentage"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              Taxa Residencial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tooSeguros.bondInsurance.pfResidentialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              Taxa Comercial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tooSeguros.bondInsurance.pfComercialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  const BondInsuranceConfigTokioMarineContentModal = () => {
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>
              Tipo de coberturas
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tokioMarine.bondInsurance.assurance"
              disabled={!userCanEdit}
              options={pottencialAssuranceOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <FieldLabel>
              LMI
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tokioMarine.bondInsurance.lmi"
              disabled={!userCanEdit}
              options={garanttiLMIOptions}
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              component={Dropdown}
              selection
              compact
              basic
              fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>
              Taxa Comissão
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tokioMarine.bondInsurance.commissionPercentage"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              Taxa Residencial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tokioMarine.bondInsurance.pfResidentialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>
              Taxa Comercial (%)
              <span style={{ color: 'red' }}>*</span>
            </FieldLabel>
            <Field
              name="insurerSpecificData.tokioMarine.bondInsurance.pfComercialTax"
              disabled={!userCanEdit}
              component={Input}
              onChange={(e: SyntheticEvent, data: any) => handlePercentageChange(data)}
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              fluid
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  const BondInsuranceConfigModal = () => {
    const { name, code } =
      availableInsurers.find((i: any) => i.id === selectedInsurer.id) ?? {};
    return (
      <div>
        <Modal
          style={{ height: 'auto', width: '50vw', zIndex: 10 }}
          closeIcon
          open={openModalConfig}
          // trigger={props.trigger}
          // onOpen={() => handleOpenModal()}
          onClose={() => setOpenModalConfig(false)}
        >
          <Header style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex' }}>
              <Icon name={'file alternate outline'} />{' '}
              {name && code && `${name} - ${code}`}
            </div>
          </Header>
          <Modal.Content scrolling>
            {selectedInsurer.code === InsurersCodeEnum.Pottencial &&
              BondInsuranceConfigPottencialContentModal()}
            {selectedInsurer.code === InsurersCodeEnum.Porto &&
              BondInsuranceConfigPortoContentModal()}
            {selectedInsurer.code === InsurersCodeEnum.PortoEssencial &&
              BondInsuranceConfigPortoEssencialContentModal()}
            {selectedInsurer.code === InsurersCodeEnum.Too &&
              BondInsuranceConfigTooContentModal()}
            {selectedInsurer.code === InsurersCodeEnum.TokioMarine &&
              BondInsuranceConfigTokioMarineContentModal()}
          </Modal.Content>
          <Modal.Actions>
            <Button color="red" onClick={() => setOpenModalConfig(false)}>
              <Icon name="remove" />
              Sair
            </Button>
            <Button color="green" onClick={() => setOpenModalConfig(false)}>
              <Icon name="checkmark" /> Salvar
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  };

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={'8'}>
          <FieldLabel>SEGURADORAS DISPONÍVEIS</FieldLabel>
          <p style={{ padding: 0, color: 'rgba(54, 68, 69, 0.5)' }}>
            Ative, desative e configure as seguradoras que deseja utilizar.{' '}
          </p>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '1rem',
            flexWrap: 'wrap'
          }}
        >
          {availableInsurers.map((insurerOption: any) => (
            <div
              key={insurerOption.id}
              style={{
                display: 'flex',
                flex: '0 0 calc((100% - 32px) / 3)',
                gap: '1rem',
                alignItems: 'center',
                marginBottom: '1rem',
                cursor: 'pointer'
              }}
              onClick={() => {
                setSelectedInsurer(insurerOption);
              }}
            >
              <InsurersImageWrapper
                isSelected={selectedInsurer && selectedInsurer.id === insurerOption.id}
                isFluid
              >
                <div style={{ width: '120px' }}>
                  <Image src={insurerOption.image} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                  >
                    <span>{insurerOption.name}</span>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'start',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      {values.bondInsurers &&
                        values.bondInsurers.find((i: any) => i.id === insurerOption.id) ? (
                        <label style={{ color: '#2185D0' }}> Ativa </label>
                      ) : (
                        <label> Inativa </label>
                      )}
                      <Toggle
                        name={insurerOption.id}
                        id={`createEstate-bondInsurance-${insurerOption.code}`}
                        disabled={!userCanEdit}
                        onChange={handleToggle}
                        checked={
                          insurerOption.code === InsurersCodeEnum.Pottencial
                            ? values.isPottencialBondInsuranceSelected
                            : insurerOption.code === InsurersCodeEnum.Garantti
                              ? values.isGaranttiBondInsuranceSelected
                              : insurerOption.code === InsurersCodeEnum.Too
                                ? values.isTooSegurosBondInsuranceSelected
                                : insurerOption.code === InsurersCodeEnum.TokioMarine
                                  ? values.isTokioMarineBondInsuranceSelected
                                  : insurerOption.code === InsurersCodeEnum.Porto
                                    ? values.isPortoTradicionalBondInsuranceSelected
                                    : insurerOption.code === InsurersCodeEnum.PortoEssencial
                                      ? values.isPortoEssencialBondInsuranceSelected
                                      : insurerOption.code === InsurersCodeEnum.Creditas
                                        ? values.isCreditasBondInsuranceSelected
                                        : false
                        }
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        border: '1px solid rgba(34, 36, 38, 0.15)',
                        borderRadius: '5px',
                        padding: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <IconButton
                        name="cog"
                        style={{ fontSize: 24, margin: '2px 0 0 0', cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedInsurer(insurerOption);
                          setOpenModalConfig(true);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </InsurersImageWrapper>
            </div>
          ))}
        </div>

        {openModalConfig &&
          (selectedInsurer?.code === InsurersCodeEnum.Creditas ? (
            <CreditasConfigModal
              value={values.insurerSpecificData.creditas.bondInsurance}
              onSave={contact =>
                setFieldValue('insurerSpecificData.creditas.bondInsurance', contact)
              }
              onClose={() => setOpenModalConfig(false)}
              disabled={!userCanEdit}
            />
          ) : (
            BondInsuranceConfigModal()
          ))}
      </Grid.Row>
    </Grid>
  );
};
